/**
 * Autenticacao das rotas de Avisos e Campanhas na API v2 (.NET 8).
 *
 * O `userToken` que o Master guarda no login e o access_token OWIN do legado
 * (api-v1): um ticket opaco, protegido por machine key, que so o legado sabe
 * abrir. A API v2 valida SOMENTE JWT (issuer/segredo compartilhados) e responde
 * 401 a qualquer outra coisa — foi exatamente o que aconteceu no POST.
 *
 * O login do legado tambem devolve `token`: um cross token (GUID) que a API v2
 * troca pelo JWT em `security/cross/{hash}` com o header `_tId`. E o mesmo
 * fluxo que o Web2 faz em App.vue. O hash e md5 de `{cross}.{yyyyMMddHH UTC}.
 * {segredo}` — muda a cada hora, por isso e calculado na hora da troca.
 *
 * A troca vai para o api-v2 resolvido pelo host (dev/homolog/prod) e NAO para
 * `broadcastApi`: o cross token vive no banco de auth do ambiente em que o
 * login foi feito. Como o JWT usa o mesmo issuer/segredo em todos os
 * ambientes, um JWT emitido pelo homolog vale numa API rodando em localhost.
 */
import axios from 'axios';
import md5 from 'md5';
import { BROADCAST_SECURITY_CROSS } from '../../../../urlConfig';

const STORAGE_KEY = 'marQAdminAuthUser';
const PRIVATE_HASH = '_6894bf30';

export const NO_CROSS_TOKEN = 'NO_CROSS_TOKEN';

const pad2 = (n) => String(n).padStart(2, '0');

/** Mesmo calculo de AuthAccessToken.ApplyHash no backend e de Web2/App.vue. */
export function crossHash(cross, now) {
  const date = now || new Date();
  const stamp =
    date.getUTCFullYear() +
    pad2(date.getUTCMonth() + 1) +
    pad2(date.getUTCDate()) +
    pad2(date.getUTCHours());
  return md5(cross + '.' + stamp + '.' + PRIVATE_HASH);
}

function readAuthUser() {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
  } catch (e) {
    return {};
  }
}

function writeAuthUser(authUser) {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(authUser));
}

function exchangeCrossToken(cross) {
  return axios
    .get(BROADCAST_SECURITY_CROSS + crossHash(cross), {
      headers: { 'Content-Type': 'application/json', _tId: cross },
    })
    .then((response) => response.data && response.data.token);
}

/**
 * JWT da API v2, trocado uma vez e guardado junto do authUser. `forceRefresh`
 * refaz a troca (usado quando a API respondeu 401 com o JWT guardado).
 * Rejeita com `NO_CROSS_TOKEN` quando o login foi feito antes de o Master
 * passar a guardar o cross token: so um novo login resolve.
 */
export function getApiV2Token(forceRefresh) {
  const authUser = readAuthUser();

  if (!forceRefresh && authUser.apiV2Token) {
    return Promise.resolve(authUser.apiV2Token);
  }

  if (!authUser.crossToken) {
    const error = new Error('Cross token ausente; refaca o login no Master.');
    error.code = NO_CROSS_TOKEN;
    return Promise.reject(error);
  }

  return exchangeCrossToken(authUser.crossToken).then((token) => {
    if (!token) {
      throw new Error('API v2 nao devolveu o token.');
    }
    const fresh = readAuthUser();
    fresh.apiV2Token = token;
    writeAuthUser(fresh);
    return token;
  });
}

function send(method, url, data, forceRefresh, options) {
  return getApiV2Token(forceRefresh).then((token) =>
    axios({
      method: method,
      url: url,
      data: data,
      // So a exportacao passa responseType ('blob'). Accept continua JSON de
      // proposito: e o que faz um ERRO da API voltar legivel em vez de binario.
      responseType: (options && options.responseType) || 'json',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
    })
  );
}

/**
 * Requisicao autenticada na API v2. Um 401 com o JWT guardado (expirado ou de
 * outro ambiente) dispara UMA nova troca e repete; o segundo 401 sobe.
 * `options.responseType` e opcional (ex.: 'blob' na exportacao).
 */
export function requestApiV2(method, url, data, options) {
  return send(method, url, data, false, options).catch((error) => {
    const status = error && error.response && error.response.status;
    if (status === 401) {
      return send(method, url, data, true, options);
    }
    throw error;
  });
}

export const isNoCrossToken = (error) => !!error && error.code === NO_CROSS_TOKEN;



// WEBPACK FOOTER //
// ./src/vue/pages/restricted/broadcast/broadcastApiV2Auth.js